import Mustache from 'mustache';

import {
  multiselectMainTemplate,
  multiselectCaptionTemplate,
  multiselectBavariaCaptionTemplate,
  multiselectOptionTemplates,
  multiselectAdditionalTemplates,
} from './multiselect-templates';
import analytics from './../analytics.js';

import {
  keyboardNavigable,
  nonRollingControlMode
} from '../mixins/keyboard-navigable'



const multiselects = {};

/**
 * Multiselect refactored
 * Supports single select and optgroups in multiselect
 *
 * Available public methods
 * addCallback(Object),
 * addOptions(Array),
 * removeOptions(Array),
 * unSelectAll(Boolean),
 * openSelect(),
 * closeSelect(),
 * anyOptionsSelected(),
 * getValue(),
 * disableMultiselect(),
 * enableMultiselect()
 *
 * @example HTML
 * // Create select element to document, and add remultiselect class amd unique ID to it
 * <select class="remultiselect" id="make" multiple>
 *  // You can add optgroup with labels or just add options
 *  <optgroup label="Suosituimmat autot">
 *    <option value="Audi">Audi</option>
 *    <option value="Saab">Saab</option>
 *  </optgroup>
 *  <optgroup label="Kaikki autot">
 *    <option value="Skoda">Skoda</option>
 *    <option value="Lada">Lada</option>
 * </select>
 *
 * @example JavaScript: Add callback to created multiselect.
 *
 * NOTE! If you overwrite onValueChange callback then no native Javasript event will be fired.
 * To top this up there can be only one event listener function listening multiselect's event per event type.
 * Better way to is add native event listener function to native select element itself.
 *
 * @example
 * window.multislects.make.element.addEventListener('change', (event) => {});
 *
 * // Available callbacks: onValueChange, onMenuOpen, onMenuClose
 * window.multiselects.make.addCallback({
 *  onValueChange : function(multiselectData, wasUnSelectAll) {
 *    console.log(multiselectData.options);
 *    // All functions available in multiselectData
 *    // I.E.:
 *    const multiselectCurrentValue = multiselectData.getValue();
 *  },
 *  onMenuOpen : function(multiselectData) {
 *    console.log('tralalaa');
 *  },
 *  onMenuClose : function(multiselectData) {
 *    console.log('closed');
 *  }
 * });
 *
 * @example JavaScript: Adding values to make select
 * // onValueChange function will fire if some of new values has selected
 * window.multiselects.make.addOptions([
 *  { value : 'volkswagen', shownValue : 'Folkkari', selected : true },
 *  { value : 'bmw',        shownValue : 'Biimeri', datasets : [{ datasetName : 'foo', datasetValue : 'bar' }] }
 * ]);
 *
 * @example JavaScript: Remove option(s) from multiselect
 * // onValueChange function will fire if some of new values has selected
 * window.multiselects.make.removeOption(['volkswagen', 'bmw']);
 *
 * @example JavaScript: Unselect all options in multiselect
 * window.multiselects.make.unSelectAll();
 *
 */
class ReMultiselect {
  /**
   * Creates multiselect.
   *
   * @param {object} element - Select element
   * @param {object} callbacks - Callbacks to bind in object
   */
  constructor(element, callbacks) {
    // Base template of multiselect
    this.template = multiselectMainTemplate;

    // Partial can be used when updating button caption
    if (document.body.dataset.brand === 'bmw' || document.body.dataset.brand === 'mini') {
      this.buttonCaptionPartial = multiselectBavariaCaptionTemplate;
    } else {
      this.buttonCaptionPartial = multiselectCaptionTemplate;
    }

    // Partial can be used when adding new options
    this.optionsPartial = multiselectOptionTemplates;

    // Used when adding new options
    this.originalOptionsTemplate = multiselectAdditionalTemplates;

    // Language phrases på svenska och suomeksi
    this.languagePhrases = {
      fi: {
        selected: 'valittu',
        select: 'Valitse',
      },
      sv: {
        selected: 'valda',
        select: 'Välj',
      },
    };

    if (typeof window.multiselects === 'undefined') {
      window.multiselects = {};
      window.activeMultiselectId = null;

      //Make object iterable
      window.multiselects[Symbol.iterator] = function* () {
        for (let key of Object.keys(window.multiselects)) {
          yield window.multiselects[key];
        }
      };
    }

    // Current value of element, returned in this.getValue()
    this.value = [];
    // Original select element
    this.element = element;

    if (typeof this.element.id === 'undefined' || this.element.id === '') {
      console.warn('Multiselect original select element must have id defined!');
      return false;
    }

    if (Object.prototype.hasOwnProperty.call(window.multiselects, this.element.id)) {
      console.warn('There is multiselect with id: ' + this.element.id + ' in dom! Could not proceed.');
      return false;
    }

    // Set language
    this.language =
      typeof window.stage_language !== 'undefined' && Object.prototype.hasOwnProperty.call(this.languagePhrases, window.stage_language)
        ? this.languagePhrases[window.stage_language]
        : this.languagePhrases.fi;

    // Callbacks includes for example onValueChange evets etc.
    this.callbacks = {
      // Default behaviour produces a native js event that can be accessed externally. This can be overridden
      onValueChange: e => {
        // There is also an eventlistener listening for the native events of this element.
        // When we create an artificial change event, it will create an infinite circular loop.
        // Setting the breakCircularEvent flag up here and down again at the event listener breaks the cycle.
        e.element.breakCircularEvent = true;
        const event = new Event('change', { bubbles: true });
        e.element.dispatchEvent(event);
      },
    };
    //this.callbacks = [];

    // selectOpened defines if current multiselect list is opened
    this.selectOpened = false;

    if (typeof callbacks === 'object' && Object.keys(callbacks).length > 0) {
      this.addCallback(callbacks);
    }

    // Select wrapper is used to point events etc.
    this.selectWrapper = null;

    // Get current options from select element
    this.selectDetails = this._collectSelectDetails();

    this._emptyValue = this.selectDetails.datasets.length ? this.selectDetails.datasets.find(dataset => dataset.datasetName === 'emptyvalue' && dataset.datasetValue) : false;
    this._addEmptyValue();

    this.options = this._optionsToJSON();

    this._renderUI();
    window.multiselects[this.element.id] = this;

    // If select is multiple, and options has first option with empty value
    // unselect all, because first is label only
    if (
      this.selectDetails.isMultiple &&
      this.options.some((option) => option.value === '' && option.selected)
    ) {
      this.unSelectAll(true);
    }

    // this.selectWrapper.addEventListener('focusin', this._onFocusIn.bind(this));

    // Update value if preselected options
    this._setValue();
    this._updateSelectButtonCaption();
    this._updateOptionsAndOptGroups();

    const selectedOptionIndex = this.options.findIndex((option) => option.selected);
    const selectedOption = this.options[selectedOptionIndex];
    if (!this.selectDetails.isMultiple && selectedOption && !selectedOption.value && !selectedOption.shownValue) {
      this.unSelectAll(false);
      this.hideOption(selectedOptionIndex);
    }

    // Get empty option indexes to hide
    const emptyOptionIndexes = this.options.reduce((acc, option, index) => {
      if (!option.value && !option.shownValue) {
        acc.push(index);
      }
      return acc;
    }, []);

    // Hide empty options
    emptyOptionIndexes.forEach((index) => {
      this.hideOption(index);
    });

    this.bindKeyboardNavitable(this.selectWrapper);
    this._updateFocusableElements();
    this.setFocusedElement(this.focusableElements[0]);
    this.setKeyboarGroupElement(this.selectWrapper);
  }


  /**
   * Push new callback function to this.callbacks
   *
   * @param {object} callbackFunctions - Callback function in object where key is type of function and content is function itself
   *
   * @returns {undefined}
   * @example
   * window.makeSelect.addCallback({
   *  onValueChange : function(event) {
   *    // Get models from ajax
   *    // Do sth
   *    window.modelSelect.updateOptions(ajaxResponse);
   *  },
   *  onMenuOpen  : function(event) {},
   *  onMenuClose : function(event) {}
   *  ...
   * })
   */
  addCallback(callbackFunctions) {
    for (const [functionName, functionContent] of Object.entries(callbackFunctions)) {
      if (typeof functionContent !== 'function') {
        console.warn('Function ' + functionName + ' is not a function!');
      } else {
        this.callbacks[functionName] = functionContent;
      }
    }
  }

  /**
   * Show or hides multiselect element
   * @param {boolean} isVisible is multiselect element shown or hidden.
   */
  show(isVisible) {
    this.element.closest('.multiselect-container').classList.toggle('hidden', !isVisible);
  }

  /**
   * Add new options to select
   * @param {array}  newOptions    - New options in object, selected parameter is not mandatory (default: false). Datasets in array.
   * @param {string|object} optGroupSpecs - If select menu uses optgroups, you can define which optgroup option is inserted to. If null, option to the bottom of the list. If object, can be used to create a new optgroup if one doesn't exist
   *
   * @returns {undefined}
   *
   * @example
   * constructedMultiselect.addOptions([
   *   { value : 'foo', shownValue : 'Foo', selected : true },
   *   { value : 'bar', shownValue : 'Bar', datasets: [{ datasetName : 'foobar', datasetValue : 'barfoo' }] }
   *  ]);
   *
   * // Datasets for value "bar" will be printed data-foobar="barfoo"
   */
  addOptions(newOptions, optGroupSpecs = { createIfNotExist: false }) {
    // Backwards compatibility. 2nd param used to be called "underOptGroup" that could be a string or NULL.
    // Now it can be NULL or string (old functionality) or an object (new functionality)
    let underOptGroup = null;
    if (typeof optGroupSpecs === 'string') {
      underOptGroup = optGroupSpecs; // Transform old style parameter to opt group name
    } else if (typeof optGroupSpecs === 'object' && optGroupSpecs !== null && optGroupSpecs.optGroupRenderData) {
      underOptGroup = optGroupSpecs.optGroupRenderData.shownValue; // New style name param to opt group name
    } else {
      // No optgroup requested
    }

    if (typeof newOptions !== 'object' || !Array.isArray(newOptions)) {
      console.error('Could not add new options. newOptions is not an array!');
      return;
    }

    // Test duplicate values
    const duplicatesCheck = [];
    newOptions.forEach(function (options) {
      duplicatesCheck.push(options.value);
    });

    // Current options
    this.options.forEach(function (options) {
      duplicatesCheck.push(options.value);
    });

    if (duplicatesCheck.length !== new Set(duplicatesCheck).size) {
      console.warn('Duplicate values detected! Could not add options to multiselect because of duplicate value. Added unqiue values only!');
      // Build array with duplicate values.
      let duplicatesToConsole = [];
      for (let i = 0; i < duplicatesCheck.length; i++) {
        const itemCountLength = duplicatesCheck.filter(function (option) {
          return option === duplicatesCheck[i];
        }).length;
        if (itemCountLength > 1) {
          duplicatesToConsole.push(duplicatesCheck[i]);
        }
      }

      duplicatesToConsole = duplicatesToConsole.filter(function (item, index, self) {
        return self.indexOf(item) === index;
      });

      // Delete duplicates from newOptions array
      newOptions = newOptions.filter(function (newOption) {
        return duplicatesToConsole.indexOf(newOption.value) === -1;
      });

      console.warn('Following values was not added: ' + duplicatesToConsole.join(', '));
    }

    // Render multiselect options
    const multiselectOptions = Mustache.render(this.optionsPartial, {
      options: newOptions,
    });

    // Render options to select-element
    const originalOptions = Mustache.render(this.originalOptionsTemplate, {
      options: newOptions,
    });

    // If underOptGroup is defined, find optGroup
    if (underOptGroup !== null && typeof underOptGroup === 'string') {
      let optGroupToInsertOriginalSelect = this.element.querySelector('optgroup[label="' + underOptGroup + '"]');
      let optGroupToInsertMultiselect = this.multiselectList.querySelector('.separator[data-separator-label="' + underOptGroup + '"]');

      if (!optGroupToInsertOriginalSelect && !optGroupToInsertMultiselect) {
        // Neither were found
        if (optGroupSpecs.createIfNotExist) {
          // A new optGroup needs to be inserted to both the original select and the multiselectlist
          // if the given optgroup doesn't already exist.

          // Firstly render the new multiselect
          const newMultiselectOptGroup = Mustache.render(this.optionsPartial, {
            options: [{ isCaption: true, ...optGroupSpecs.optGroupRenderData }],
          });

          // Secondly create a new optgroup element for the original select
          const optGroup = document.createElement('optgroup');
          optGroup.setAttribute('label', optGroupSpecs.optGroupRenderData.shownValue);
          Object.entries(optGroupSpecs.optGroupRenderData).map(([key, val]) => {
            // No idea if there are other attributes, but if there are, put them there
            optGroup.setAttribute(key, val);
          });

          // Insert opt group to select DOM
          optGroupToInsertOriginalSelect = this.element.appendChild(optGroup);

          // Insert multiselect opt group to multiselect DOM
          this.multiselectList.insertAdjacentHTML('beforeend', newMultiselectOptGroup);
          optGroupToInsertMultiselect = this.multiselectList.querySelector('.separator[data-separator-label="' + underOptGroup + '"]');
        }
      }

      // Using nextUntil function from ie-polyfills
      let multiselectOptGroupLastElement = this._nextUntil(optGroupToInsertMultiselect, '.separator');
      if (Array.isArray(multiselectOptGroupLastElement) && multiselectOptGroupLastElement.length < 1) {
        // Special case: trying to add something to the last of the list, there isn't a .separator there.
        multiselectOptGroupLastElement = [optGroupToInsertMultiselect];
      }
      // Both element found, add options to 'em
      if (/*optGroupToInsertMultiselect && */ optGroupToInsertOriginalSelect && multiselectOptGroupLastElement[0]) {
        multiselectOptGroupLastElement[0].insertAdjacentHTML('afterend', multiselectOptions);
        optGroupToInsertOriginalSelect.insertAdjacentHTML('beforeend', originalOptions);
      } else {
        console.error('Could not find optgroup! Exiting!');
        return;
      }
    } else {
      this.element.insertAdjacentHTML('beforeend', originalOptions);
      this.multiselectList.insertAdjacentHTML('beforeend', multiselectOptions);
    }

    // Add click events to options
    this.options = this._optionsToJSON();
    this._bindOptionClicks(newOptions);

    this._setValue();

    // Update caption in case of newOptions contains options selected
    this._updateSelectButtonCaption();

    // If any options selected, show clearAll button
    if (this.anyOptionsSelected()) {
      this._showClearAll();
    }

    this._updateOptionsAndOptGroups();
    this._updateFocusableElements();
  }

  /**
   * Remove all options from multiselect
   * @param {boolean} runCallBack     - If true, method will run onValueChange callback
   * @param {boolean} removeEmptyOptGroup - If true, method will also remove an optGroup that become empty after removing the last option   *
   */
  removeAllOptions( runCallBack = true, removeEmptyOptGroup = false) {
    this.unSelectAll(false);
    const optionValues = this.options.filter( option => option.value !== '' ).map( option => option.value )
    this.removeOptions(optionValues, runCallBack, removeEmptyOptGroup);
  }

  /**
   * Removes options from multiselect and original select
   *
   * @param {array}   optionsToRemove - Removes options from original select and new select
   * @param {boolean} runCallBack     - If true, method will run onValueChange callback
   * @param {boolean} removeEmptyOptGroup - If true, method will also remove an optGroup that become empty after removing the last option
   *
   * @returns {undefined}
   *
   * @example
   * constructedMultiselect.removeOptions([
   *   'value1', 'value2'
   * ])
   */
  removeOptions(optionsToRemove, runCallBack = true, removeEmptyOptGroup = false) {
    if (typeof optionsToRemove !== 'object' || !Array.isArray(optionsToRemove)) {
      console.error('optionsToRemove was not array!');
      return;
    }

    // If there is options that are selected, this flag will be
    // true and onValueChange will fire if function is defined
    let valueChanged = false;

    // Iterate options to be removed
    for (const optionValue of optionsToRemove) {
      const optionToBeDeleted = this.options.findIndex(function (option) {
        // Check if option is selected
        if (!valueChanged && option.selected && option.value === optionValue) {
          valueChanged = true;
        }
        return option.value === optionValue;
      });

      if (optionToBeDeleted !== -1) {
        // Delete select and multiselect elements
        const originalSelectOption = this.element.querySelector('[value="' + this.options[optionToBeDeleted].value + '"]'),
          multiselectOption = this.multiselectList.querySelector('[data-value="' + this.options[optionToBeDeleted].value + '"]');

        if (originalSelectOption && originalSelectOption.parentNode.tagName === 'OPTGROUP') {
          // The option is a child of an optgroup, not the main select.
          // This option has to be removed from the optgroup.
          const originalSelectOptGroup = originalSelectOption.parentNode;
          originalSelectOptGroup.removeChild(originalSelectOption);
          if (removeEmptyOptGroup && originalSelectOptGroup.childElementCount < 1) {
            // Computer, initiate the auto destruct sequence. Authorization code: zero zero zero. Destruct. Zero.
            const multiSelectOptGroupLabel = this.multiselectList.querySelector(`[data-separator-label="${originalSelectOptGroup.label}"]`);

            try {
              this.element.removeChild(originalSelectOptGroup);
              this.multiselectList.removeChild(multiSelectOptGroupLabel);
            } catch (e) {
              console.log('Could not remove optgroup elements');
            }
          }
        } else {
          // Remove option from the direct parent select DOM-elements
          this.element.removeChild(originalSelectOption);
        }
        // The pseudo "optgroup" in the constructed MultiSelect is a flat structure,
        // the optgroup options there are siblings of non-grouped options, so this can be removed directly
        this.multiselectList.removeChild(multiselectOption);
        // Delete value from options array
        delete this.options[optionToBeDeleted];
      } else {
        console.warn('Could not find select option with value ' + optionValue);
      }

      if (runCallBack && valueChanged && typeof this.callbacks.onValueChange === 'function') {
        // Fire onValueChange function
        this.callbacks.onValueChange(this);
      }
      // Delete empty array elements
      this.options = this.options.filter(Boolean);
    }

    this._setValue();
    this._updateSelectButtonCaption();
    this._updateOptionsAndOptGroups();
    this._updateFocusableElements();
  }

  /**
   * Select option values
   *
   * @param {optionValues} optionValues - Option values to select in array
   * @param {bool}         dontCare     - wheter we care if it's selected already or not
   *
   * @example
   * window.multiselects.makes.selectOption([
   *  'Audi', 'BMW'
   * ]);
   */
  selectOptions(optionValues, dontCare = false) {
    if (typeof optionValues !== 'object' && !Array.isArray(optionValues)) {
      console.error('optionValues is not an array!');
      return;
    }

    for (let i = 0; i < optionValues.length; i++) {
      if (this.element.querySelector('[value="' + optionValues[i] + '"]')) {
        const originalSelectOption = this.element.querySelector('[value="' + optionValues[i] + '"]'),
          multiselectOption = this.multiselectList.querySelector('[data-value="' + optionValues[i] + '"]');

        if (!originalSelectOption.selected || dontCare) {
          // Emulate option click
          multiselectOption.click();
        }
      }
    }
  }

  /**
   * Unselects all options from the multiselect
   *
   * @param {boolean} runCallBack - If false, callback will not run. Needed for example in single select list option click
   *
   * @returns {undefined}
   */
  unSelectAll(runCallBack = true) {
    this.options.forEach(option => {
      this._unSelectOption(option);
    });

    this._setValue();
    this._hideClearAll();
    // Run onValueChange if exists
    if (typeof this.callbacks.onValueChange === 'function' && runCallBack) {
      this.callbacks.onValueChange(this, true);
    }

    this.handleMinMaxSelectors();
    this._updateSelectButtonCaption();
  }

  /**
   * Unselect option element
   * @param {HTMLElement} option element from this.options array
   * @param {boolean} runCallBack If false, callback will not run. Needed for example in single select list option click
   */
  unSelect(option, runCallBack) {
    if (option && option.selected) {
      this._unSelectOption(option);

      this._setValue();

      // Run onValueChange if exists
      if (typeof this.callbacks.onValueChange === 'function' && runCallBack) {
        this.callbacks.onValueChange(this, true);
      }

      this.handleMinMaxSelectors();
      this._updateSelectButtonCaption();
    }
  }

  /**
   * Opens multiselect UI and close existing multiselect if document has opened
   *
   * @returns {undefined}
   */
  openSelect() {
    // If there is already active multiselect in dom, close it
    if (window.activeMultiselectId !== null && window.activeMultiselectId !== this.element.id) {
      window.multiselects[window.activeMultiselectId].closeSelect();
    }

    if (window.activeMultiselectId !== this.element.id) {
      this.focusSearchInput();
    }

    window.activeMultiselectId = this.element.id;
    this.selectWrapper.classList.add('in');
    this.multiselectButton.setAttribute('aria-expanded', 'true');
    this.multiselectListContainer.setAttribute('aria-hidden', 'false');

    // If menuOpen callback is defined
    if (typeof this.callbacks.onMenuOpen === 'function') {
      this.callbacks.onMenuOpen(this);
    }

    if (this.multiselectButton.classList.contains('value-selected')) {
      this._showClearAll();
    }

    this.setFocusedElement(this.focusableElements[0]);
  }

  /**
   * Move focus to search input
   */
  focusSearchInput() {
    if (this.element.classList.contains('add-search') && this.searchInput) {
      setTimeout(() => {
        this.setFocusedElement(this.focusableElements[0]);
      }, 200)
    }
  }

  /**
   * Closes multiselect UI
   *
   * @returns {undefined}
   */
  closeSelect() {

    window.activeMultiselectId = null;
    this.selectWrapper.classList.remove('in');
    this.multiselectButton.setAttribute('aria-expanded', 'false');
    this.multiselectListContainer.setAttribute('aria-hidden', 'true');

    if (typeof this.callbacks.onMenuClose === 'function') {
      this.callbacks.onMenuClose(this);
    }

    this._hideClearAll();
    this.clearSearchInput();
  }

  /**
   * Returns true if one or more options are selected
   *
   * @returns {boolean}
   */
  anyOptionsSelected() {
    return this.options.some(function (option) {
      return option.selected === true;
    });
  }

  /**
   * Return selected values in array
   *
   * @returns {array} - values in array list
   */
  getValue() {
    return this.value.slice(); // to not return reference to this value. This bit my ass earlier
  }

  /**
   * If needed only latest selection details (value and wasSelected boolean), this method returns it
   *
   * @returns {object}
   */
  getLatestSelection() {
    return this.selectDetails.latestSelection;
  }

  /**
   * Disable multiselect element
   *
   * @returns {undefined}
   */
  disableMultiselect() {
    if (!this.multiselectButton.classList.contains('disabled')) {
      this.multiselectButton.classList.add('disabled');
      this.selectDetails.isDisabled = true;
      this.element.disabled = true;
      this._hideClearAll();
      if (window.activeMultiselectId === this.element.id) {
        this.closeSelect();
      }
    }
  }

  /**
   * Enable multiselect element
   *
   * @returns {undefined}
   */
  enableMultiselect() {
    if (this.multiselectButton.classList.contains('disabled')) {
      this.multiselectButton.classList.remove('disabled');
      this.selectDetails.isDisabled = false;
      this.element.disabled = false;
      if (this.anyOptionsSelected()) {
        this._showClearAll();
      }
    }
  }

  /**
   * Enable multiselect element
   * @param {boolean} enabled - enable or disable multiselect
   */
  toggleMultiselect( enabled ) {
    if( enabled ) {
      this.enableMultiselect();
    } else {
      this.disableMultiselect();
    }
  }

  /**
   * Handles max selector item disabling based on min selector value
   *
   * @example
   * <select class="remultiselect" name="pricemin" id="filter-pricemin"
   *         data-less-than="filter-pricemax">
   * <select class="remultiselect" name="pricemax" id="filter-pricemax"
   *         data-greater-than="filter-pricemin">
   * @returns {undefined}
   */
  handleMinMaxSelectors() {
    const currentValue = parseInt(this.getValue()[0]) || null,
      { lessThan, greaterThan } = this.element.dataset,
      pair = lessThan || greaterThan;

    if (!pair) return false;

    const pairSelector = window.multiselects[pair];
    if (!pairSelector) return false;
    const pairOptions = pairSelector.element.options;

    if (currentValue) {
      const pairValue = parseInt(pairSelector.getValue()[0]);

      if (pairValue) {
        if (lessThan && pairValue < currentValue) {
          pairSelector.unSelectAll();
        } else if (greaterThan && pairValue > currentValue) {
          pairSelector.unSelectAll();
        }
      }

      for (let i = 0; i < pairOptions.length; i++) {
        const value = parseInt(pairOptions[i].value);
        if (lessThan) {
          if (value && value < currentValue) {
            pairSelector.disableOption(i);
          } else pairSelector.enableOption(i);
        } else if (greaterThan) {
          if (value && value > currentValue) {
            pairSelector.disableOption(i);
          } else pairSelector.enableOption(i);
        }
      }
    } else {
      for (let i = 0; i < pairOptions.length; i++) {
        pairSelector.enableOption(i);
      }
    }
  }

  disableOption(index) {
    this.multiselectList.children[index].classList.add('disabled');
    this.element.optionsAndOptgroups[index].disabled = true;
  }

  enableOption(index) {
    this.multiselectList.children[index].classList.remove('disabled');
    this.element.optionsAndOptgroups[index].disabled = false;
  }

  /**
   * Show option by index
   * @param {number} index - index of target option
   * @param {boolean} [show] show or hide option, defaults to true
   */
  showOption(index, show = true) {
    const option = this.element.optionsAndOptgroups[index];

    if (option.value && option.innerText) {
      this.multiselectList.children[index].classList.toggle('hidden', !show);
      this.element.optionsAndOptgroups[index].hidden = !show;
    }
  }

  /**
   * Hide option by index
   * @param {number} index - index of target option
   */
  hideOption(index) {
    this.multiselectList.children[index].classList.add('hidden');
    this.element.optionsAndOptgroups[index].hidden = true;
  }

  /**
   * Clear search filter if it exists and show all list options
   */
  clearSearchInput() {
    if (this.searchInput) {
      this.searchInput.value = '';
      // clear filtering by triggering change event.
      const event = new Event('change');
      this.searchInput.dispatchEvent(event);
      this._updateFocusableElements();
      this.searchInput.setAttribute('placeholder', '');
    }
  }

  /**
   * Filter option group option elements
   * @param {string} groupName option group name
   * @return {HTMLElement[]} option elements
   */
  filterOptionGroupOptions(groupName) {
    return this.options.filter(option => {
      const dataset = option.datasets.find(dataset => dataset.datasetName === 'group');
      return dataset.datasetValue === groupName;
    });
  }

  /**
   *  List all option group names
   * @return {string} option group names
   */
  listAllGroupNames() {
    const names = new Set();
    try {
      this.options.forEach(option => {
        names.add(option.datasets[0].datasetValue);
      });
    } catch (e) {
      // If datasets weren't used, see if optgroups still exist, and list their labels.
      for (const optgroup of this.element.querySelectorAll('optgroup')) {
        // If has an optGroup non-standard attribute "value", use that. Else fallback to label
        const val = optgroup.getAttribute('value');
        names.add(val || optgroup.label);
      }
    }
    return Array.from(names);
  }

  /**
   * Show option group by option group name
   *
   * @example
   * Give a name for optgrou element like this ```
   * <optgroup data-group="preresented">
   *   <option data-group="preresendted">BMW</option>
   * </optgroup>
   * ```
   * Call window.multiselects['brands-filter'].toggleOptionGroup('preresented', true)
   *
   * @param {string} groupName option group name
   * @param {boolean} show pass true to show options, false to hide options
   */
  toggleOptionGroup(groupName, show) {
    const groups = this.filterOptionGroupOptions(groupName);
    //Basic showOption & hideOption() won't work due this.options holds <optgroup> element information.
    //Indexes in this.element.options and this.options mismatch
    groups.forEach(option => {
      const index = this.options.findIndex(_option => _option.value === option.value);
      this.multiselectList.children[index].classList.toggle('hidden', !show);

      if (!show) {
        this.unSelect(option, false);
      }
    });

    //Update native select element status
    Array.from(this.element.options).forEach(option => {
      if (option.dataset.group === groupName) {
        option.hidden = !show;
      }
    });
  }

  /* * * * * * * * * * *
   * Private functions *
   * * * * * * * * * * */

  /**
   * Called when element is focused
   * @param {Event} event event object
   */
  _onFocusIn(event) {
    if (this.searchInput) {
      let placeholder = '';

      //Update placeholder value on keyboard navigation
      if (event.target?.classList.contains('group-option')) {
        const hoveredOption = this.options.find((option) => option.value === event.target.dataset.value && !option.selected);
        const selectedOptions = this.selectDetails.isMultiple ? this.options.filter((option) => option.selected && (option.value !== event.target.dataset.value)) : [];
        placeholder = [hoveredOption, ...selectedOptions].filter(Boolean).map((option) => option?.shownValue).join(', ')
      }

      this.searchInput.setAttribute('placeholder', placeholder);
    }
  }

  /**
   * Called when a element loses it focus
   */
  _tabOut() {
    this.closeSelect();
  }

  /**
   * Update keyboard focusable elements
   * @private
   */
  _updateFocusableElements() {
    const elements = this.selectWrapper.querySelectorAll('input.multiselect-container-search-input,li.group-option[tabindex="-1"]:not(.hidden)');
    this.setFocusaleElements(elements);
  }

  /**
   * Called when enter key is pressed
   * @param {HTMLElement} focucedElement focused element
   */
  _onEnterPressed(focucedElement) {
    const value = focucedElement.dataset.value;

    if (value) {
      this.selectOptions([value], true);
    }
  }

  /**
   * Creates an option for set empty value
   * @private
   */
  _addEmptyValue() {
    if (this._emptyValue) {
      const selected = !this.element.querySelectorAll('option[selected]').length;
      const emptyValueOption = new Option(this._emptyValue.datasetValue, '', true, selected);
      this.element.prepend(emptyValueOption);
    }
  }

  /**
   * Performs unselect operation
   * @private
   * @param {object} option element from this.options array
   */
  _unSelectOption(option) {
    if (option.selected) {
      const originalSelectOption = this.element.querySelector('[value="' + option.value + '"]');
      const multiselectOption = this.multiselectList.querySelector('[data-value="' + option.value + '"]');

      multiselectOption.classList.remove('selected');
      originalSelectOption.selected = false;
      originalSelectOption.removeAttribute('selected');

      option.selected = false;
    }
  }

  /**
   * Set value to this.value
   *
   * @return {undefined}
   */
  _setValue() {
    for (const option of this.options) {
      const optionIndex = this.value.indexOf(option.value);

      if (option.selected) {
        if (optionIndex === -1) {
          // Push option value to selected values, if it's selected but not yet pushed
          this.value.push(option.value);
        }
      } else if (optionIndex >= 0) {
        // Delete option value from selected values, if it's not selected but was pushed before
        this.value.splice(optionIndex, 1);
      }
    }

    this.handleMinMaxSelectors();
  }

  /**
   * Shows clear all icon at the top right corner
   *
   * @returns {undefined}
   * @private
   */
  _showClearAll() {
    const self = this;
    if (typeof this.element.dataset.noClear === 'undefined' && !this.selectDetails.isMultiple) {
      self.clearAllButton.classList.add('show');
    }
  }

  /**
   * Hides clear all icon from the top right corner
   *
   * @returns {undefined}
   * @private
   */
  _hideClearAll() {
    this.clearAllButton.classList.remove('show');
  }

  /**
   * Pick datasets from DOMStringMap to object
   * Format dataset for right format to be passed to mustache template
   * @param {HTMLElement} element select, optgroup, option element
   * @return {object[]} datasets array
   */
  _formatDataSet(element) {
    let datasets = [];

    for (const [key, value] of Object.entries(Object.assign({}, element.dataset))) {
      datasets.push({ datasetName: key, datasetValue: value });
    }

    return datasets;
  }

  /**
   * Get options from select and return option values and shown values in object
   *
   * @returns {object}
   * @private
   */
  _optionsToJSON() {
    const optGroups = this.element.querySelectorAll('optgroup');
    const optionsArray = [];

    if (optGroups.length === 0) {
      const optionElements = this.element.querySelectorAll('option');
      for (const optionElement of optionElements) {
        const datasets = this._formatDataSet(optionElement);
        const color = datasets.find(dataset => dataset.datasetName === 'color')?.datasetValue || '';
        const colorOutline = datasets.find(dataset => dataset.datasetName === 'colorOutline')?.datasetValue || '';

        optionsArray.push({
          value: optionElement.value,
          shownValue: optionElement.innerText,
          selected: optionElement.selected,
          hidden: optionElement.hidden,
          underOptionGroup: false,
          datasets,
          color,
          colorOutline,
        });
      }
    } else {
      for (const optGroup of optGroups) {
        const datasets = this._formatDataSet(optGroup);
        // Push optGroup option to optionsArray
        optionsArray.push({
          value: optGroup.label,
          shownValue: optGroup.label,
          selected: false,
          isCaption: true,
          classes: optGroup.dataset.classes,
          datasets,
        });

        // Get optGroup options and push 'em next to optGroup caption
        const optionElements = optGroup.querySelectorAll('option');
        for (const optionElement of optionElements) {
          const datasets = this._formatDataSet(optionElement);
          const color = datasets.find(dataset => dataset.datasetName === 'color')?.datasetValue || '';
          const colorOutline = datasets.find(dataset => dataset.datasetName === 'colorOutline')?.datasetValue || '';

          optionsArray.push({
            value: optionElement.value,
            shownValue: optionElement.innerText,
            selected: optionElement.selected,
            isCaption: false,
            hidden: optionElement.hidden,
            underOptionGroup: true,
            datasets,
            color,
            colorOutline,
          });
        }
      }
    }

    return optionsArray;
  }

  /**
   * Binds multiselect basic ui events
   *
   * @returns {undefined}
   * @private
   */
  _bindEvents() {
    const self = this;

    this.element.addEventListener('change', function (e) {
      if (self.element.breakCircularEvent) {
        self.element.breakCircularEvent = false;
      } else {
        const options = Array.prototype.slice.call(self.element.options);
        self.selectOptions(options.filter(option => option.selected === true).map(option => option.value), true);
      }
    });

    // Toggle menu on button click
    this.multiselectButton.addEventListener('click', () => {
      // If multiselect is disabled, do nothing
      if (self.selectDetails.isDisabled) {
        return;
      }
      // If multiselect is disabled
      if (!self.selectWrapper.classList.contains('in')) {
        self.openSelect();
      } else {
        self.closeSelect();
      }
    });

    // If accept button exists in select list (multiselect only)
    // bind close event to it
    if (this.acceptButton) {
      this.acceptButton.addEventListener('click', function () {
        self.closeSelect();
      });
    }

    // Clear selection event listener
    this.clearAllButton.addEventListener('click', function () {
      if (window.activeMultiselectId === self.element.id) {
        self.closeSelect();
      }
      self.unSelectAll();
      self.clearSearchInput();
    });

    // Window body click event to close active multiselect
    if (typeof window.multiselectBodyClickEventDefined === 'undefined') {
      document.documentElement.addEventListener('click', function (event) {
        if (window.activeMultiselectId !== null && !event.target.closest('.multiselect-container.in')) {
          window.multiselects[window.activeMultiselectId].closeSelect();
        }
        return;
      });
      window.multiselectBodyClickEventDefined = true;
    }


    // Bind option clicks
    this._bindOptionClicks(this.options);

    // Bind search
    if (this.searchInput) {

      this.searchInput.addEventListener('click', (e) => {
        this.setFocusedElement(this.searchInput);
      });

      this.searchInput.addEventListener('keyup', function (e) {
        self.filterSelectList(e);
      });
      this.searchInput.addEventListener('change', function (e) {
        self.filterSelectList(e);
      });
    }

    if (this.searchWrap) {
      this.searchWrap.addEventListener('click', (event) => {
        if (event.target.tagName.toLowerCase() !== 'input') {
          this.closeSelect();
        }
      });
    }
  } // bindEvents

  filterSelectList(event) {

    const searchString = this.searchInput.value ? this.searchInput.value.toLowerCase() : '';

    const options = this.options;

    if (options && options.length) {
      for (let i = 0; i < options.length; i++) {

        if (options[i].isCaption) {
          continue;//Skip <optgroup> elements here.
        }

        // show / hide list elements
        if (!searchString) {
          this.showOption(i);
        } else {
          const optionValue = options[i].value ? options[i].value.toLowerCase() : '';
          const optionText = options[i].shownValue ? options[i].shownValue.toLowerCase() : '';
          const matchFound = (optionValue.search(searchString) > -1 || optionText.search(searchString) > -1);

          this.showOption(i, matchFound);
        }
      }

      //Here we hide <optgroup> that don't have any visible childrens
      let visibleChildernCount = 0;

      for (let i = options.length - 1; i >= 0; i--) {

        if (!options[i].isCaption && !this.element.optionsAndOptgroups[i].hidden) {
          visibleChildernCount++;
        }

        if (options[i].isCaption) {
          this.showOption(i, visibleChildernCount > 0);
          visibleChildernCount = 0;
        }
      }
    }

    this._updateFocusableElements();
  }

  /**
   * Select option
   *
   * @param {object} option - Option object [window.multiselects.option[0]]
   */
  selectOption(option) {
    if (!this.selectDetails.isMultiple) {
      // Unselect last possible option if select is not multiple
      this.unSelect(this.options.find(o => o.selected), false);
    }
    this.options.find(o => o.value === option.value).selected = true;

    this.element.querySelector(`option[value="${option.value}"]`).selected = true;
    this.element.querySelector(`option[value="${option.value}"]`).setAttribute('selected', '');
    this.multiselectList.querySelector(`[data-value="${option.value}"]`).classList.add('selected');

    this._setValue();
    this._pushAnalytics();
    this._updateSelectButtonCaption();
  }

  /**
   * Pushes selected data to the dataLayer
   */
  _pushAnalytics() {
    if (this.element.dataset.analyticsName && this.element.dataset.analyticsLabel) {
      const showCaptions = (this.element.dataset.analyticsShowCaptions === 'true');
      const info = [this.element.dataset.analyticsName, this.element.dataset.analyticsLabel].filter(Boolean);

      let values = [];

      // Set group caption as a prefix for the value
      if (showCaptions) {
        values = this.getValue().map((value) => {
          let optionsCaption = null;
          let isValue = false;

          // Loop values in reverse so we can get the caption for the selected values since they appear before them
          const valuesReversed = this.options.slice().reverse();
          for (let i = 0; i < valuesReversed.length; i++) {
            if (valuesReversed[i].value === value) {
              isValue = true;
            }

            // Get the caption
            if (isValue && valuesReversed[i].isCaption) {
              optionsCaption = valuesReversed[i].shownValue;
              isValue = false;

              // Break the loop when we already have the caption
              break;
            }
          }

          // Set the caption as a prefix
          if (optionsCaption) {
            value = `${optionsCaption} ${value}`;
          }

          return value;
        });
      } else {
        // Get set values and set the shownValue as value
        values = this.getValue().map((value) => {
          return this.options.find((option) => option.value === value)?.shownValue;
        }).filter(Boolean);
      }

      // Set the analytics data if there are values
      if (values.length) {
        const data = {
          element: 'select',
          elementName: `UI - ${info.join(' - ')} | ${values.join(', ')}`,
          type: 'change'
        };

        analytics.pushUIInteractionAnalytics(data);
      }
    }
  }

  /**
   * Creates list items click events
   *
   * @param {object} selectOptions - Options in JSON
   *
   * @returns {undefined}
   */
  _bindOptionClicks(selectOptions) {
    const self = this;

    // Loop selectOptions
    for (const selectOption of selectOptions) {
      const listItem = this.multiselectList.querySelector('li[data-value="' + selectOption.value + '"]');

      // If select option is optgroup caption, continue loop
      if (selectOption.isCaption) {
        continue;
      }

      if (listItem) {

        if (self.searchInput) {
          listItem.addEventListener('mouseover', (event) => {
            const hoveredOption = this.options.find((option) => option.value === listItem.dataset.value && !option.selected);
            const selectedOptions = this.selectDetails.isMultiple ? this.options.filter((option) => option.selected && option.value !== listItem.dataset.value) : [];
            const placeholder = [hoveredOption, ...selectedOptions].filter(Boolean).map((option) => option?.shownValue).join(', ');

            self.searchInput.setAttribute('placeholder', placeholder);
          });

          listItem.addEventListener('mouseout', (event) => {
            self.searchInput.setAttribute('placeholder', '');
          });
        }

        listItem.addEventListener('click', function (event) {
          let targetElement = event.currentTarget;

          // Do not mind disabled options
          if (targetElement.dataset?.disabled) {
            return;
          }
          // Get original select option using the targetElement's data-value, not the closure variable
          const originalSelectOption = self.element.querySelector('option[value="' + targetElement.dataset.value + '"]');
          const isSelected = targetElement.classList.contains('selected');

          // If select list is not multiple, unselect all
          if (!self.selectDetails.isMultiple) {
            // If user is clicking currently selected value, return
            const currentValue = self.getValue();
            if (currentValue.length === 1 && currentValue[0].toUpperCase() === targetElement.dataset.value.toUpperCase()) {
              self.unSelectAll(true); //Multiselect has usually own close dropwon button, we don't use it..
              self.closeSelect();
              return;
            }

            // Remove all selection without onValueChange callback
            self.unSelectAll(false);

            // If user selected option without value, return
            if (targetElement.dataset.value === '' && !self._emptyValue) {
              self.closeSelect();
              if (typeof self.callbacks.onValueChange === 'function') {
                self.callbacks.onValueChange(self, true);
              }
              return;
            }
          }
          // Set latest value to selectDetails
          self.selectDetails.latestSelection.value = targetElement.dataset.value;

          // Selecting option
          if (isSelected) {
            targetElement.classList.remove('selected');
            originalSelectOption.selected = false;
            originalSelectOption.removeAttribute('selected');
            self.selectDetails.latestSelection.wasUnChecked = true;
          } else {
            targetElement.classList.add('selected');
            originalSelectOption.selected = true;
            originalSelectOption.setAttribute('selected', '');
            self.selectDetails.latestSelection.wasUnChecked = false;
          }

          // Update options
          self.options = self._optionsToJSON();

          // If select is single-select, hide after option click
          if (!self.selectDetails.isMultiple) {
            self.closeSelect();
          }

          // Update this.value and button captions
          self._setValue();
          self._updateSelectButtonCaption();
          self._pushAnalytics();

          // Execute onValueChange function if exists
          if (typeof self.callbacks.onValueChange === 'function') {
            self.callbacks.onValueChange(self);
          }
        });
      } else {
        console.warn('Could not find list items with data-value "' + selectOption.value + '" in list "' + this.multiselectList.id + '".');
      }
    }
  }

  /**
   * Renders multiselect template and place it to DOM
   *
   * @returns {undefined}
   * @private
   */
  _renderUI() {
    const objectToMustache = {
      selectDetails: this.selectDetails,
      options: this.options,
      showClearAll: typeof this.element.dataset.noClear === 'undefined' && this.anyOptionsSelected() ? true : false,
      addSearch: this.element.classList.value.split(' ').includes('add-search'),
    };

    // Create wrapper div
    const wrapper = document.createElement('div');
    wrapper.className = `multiselect-container ${this.selectDetails.type}`;

    if( this._hasOptGroups() ) {
      wrapper.classList.add('multiselect-container-optgroups');
    }

    // Append wrapper before old select
    this.element.parentNode.insertBefore(wrapper, this.element.nextSibling);

    // Delete original select
    this.element.parentNode.removeChild(this.element);

    // Append original select and remultiselect ui to wrapper
    wrapper.appendChild(this.element);

    // Append mustache rendered template to wrapper
    wrapper.innerHTML += Mustache.render(this.template, objectToMustache, {
      buttonCaption: this.buttonCaptionPartial,
      optionsList: this.optionsPartial,
    });

    this.selectWrapper = wrapper;

    // Get appended element to this.element
    this.element = this.selectWrapper.querySelector('select.remultiselect');
    this.element.setAttribute('tabindex', '-1');

    // Save multiselect button and list container for later use
    this.multiselectButton = this.selectWrapper.querySelector('.multiselect-open');
    this.multiselectListContainer = this.selectWrapper.querySelector('.list-container');
    this.multiselectList = this.multiselectListContainer.querySelector('ul');
    this.clearAllButton = this.selectWrapper.querySelector('.clear-selection');
    this.acceptButton = this.selectWrapper.querySelector('.close-button');

    // elements for searching multiselect list
    this.searchWrap = this.selectWrapper.querySelector('.multiselect-container-search');
    this.searchInput = this.selectWrapper.querySelector('.multiselect-container-search-input');
    this.searchStatus = this.selectWrapper.querySelector('.multiselect-container-search-status');

    this.multiselectButton.setAttribute('aria-expanded', 'false');

    // Bind events
    this._bindEvents();
  }

  /**
   * Updates select button caption
   *
   * @returns {undefined}
   * @private
   */
  _updateSelectButtonCaption() {
    const currentValue = this.getValue();

    if (currentValue.length > 0) {

      const selectedOptions = currentValue
        .map(value => this.options.find(option => option.value === value)) // Get options which match current value(s)
        .map(option => option.shownValue); // Get shownValue properties of matching options

      if (!selectedOptions || !selectedOptions.length) {
        this.multiselectButton.innerHTML = Mustache.render(this.buttonCaptionPartial, {
          defaultCaption: this.element.classList.contains('show-caption') ? this.selectDetails.defaultCaption : '',
        });
      } else {
        // Prepend ', ' to all options but the first
        const captions = selectedOptions.map((option, index) => (index !== 0 ? ', ' + option : option));

        this.multiselectButton.innerHTML = Mustache.render(this.buttonCaptionPartial, {
          optionsSelected: true,
          defaultCaption: this.element.classList.contains('show-caption') ? this.selectDetails.defaultCaption : '',
          caption: captions,
          color: this.options.find(option => option.value === currentValue[0])?.color,
          colorOutline: this.options.find(option => option.value === currentValue[0])?.colorOutline,
        });

        this._showOverflowCount();

        if (!this.multiselectButton.classList.contains('value-selected')) {
          this.multiselectButton.classList.add('value-selected');
          if (!this.clearAllButton.classList.contains('clear-selection-unactive') && !this.selectDetails.isMultiple) {
            this.multiselectListContainer.classList.add('clear-button-visible');
          }
        }
      }
    } else {
      this.multiselectButton.innerHTML = Mustache.render(this.buttonCaptionPartial, {
        defaultCaption: this.selectDetails.defaultCaption,
      });

      if (this.multiselectButton.classList.contains('value-selected')) {
        this.multiselectButton.classList.remove('value-selected');
        this.multiselectListContainer.classList.remove('clear-button-visible');
        this._hideClearAll();
      }

      this.multiselectButton.removeAttribute('data-overflow-count');
      this.multiselectButton.classList.remove('show-overflow-count');
    }
  }

  _showOverflowCount() {
    let buttonSpan = this.multiselectButton.children[0],
      totalWidth = 0,
      overflowCount = 0;

    for (let i = 0; i < buttonSpan.children.length; i++) {
      totalWidth += buttonSpan.children[i].offsetWidth;

      if (totalWidth > buttonSpan.offsetWidth) {
        overflowCount = buttonSpan.children.length - i;
        break;
      }
    }

    this.multiselectButton.setAttribute('data-overflow-count', '+' + overflowCount);

    if (overflowCount && buttonSpan.children.length > 1) {
      this.multiselectButton.classList.add('show-overflow-count');
    } else {
      this.multiselectButton.classList.remove('show-overflow-count');
    }
  }

  _updateOptionsAndOptGroups() {

    const optionsAndOptgroups = [];

    //Search optgroup element from native HTMLSelectElement
    const optionGroups = this.element.querySelectorAll('optgroup');

    //This is done for get the index to match when options are disabled, enabled, shown or hidden
    if (optionGroups.length > 0) {
      optionGroups.forEach(optGroup => {
        optionsAndOptgroups.push(optGroup);
        optGroup.querySelectorAll('option').forEach(option => {
          optionsAndOptgroups.push(option);
        });
      });
    } else {//No optgroup elements available
      this.element.querySelectorAll('option').forEach(option => {
        optionsAndOptgroups.push(option);
      });
    }


    this.element.optionsAndOptgroups = optionsAndOptgroups;
  }

  /**
   * Collects select details to object which can be used in mustache render
   *
   * @returns {object}
   * @private
   */
  _collectSelectDetails() {
    const selectDetails = {
      name: this.element.name,
      form: this.element.form,
      type: this.element.dataset.type,
      isDisabled: this.element.disabled,
      isMultiple: this.element.type === 'select-multiple', // If select is multiple
      datasets: this._formatDataSet(this.element),
      searchPlaceholder: this.element.dataset.searchPlaceholder,
      defaultCaption: this.element.dataset.caption ?? this.language.select,
      classes: this.element.className.replace('remultiselect', ''),
      latestSelection: {
        wasUnChecked: false, // Defines if last selection was unselect
        value: null, // Last selected / unselected value
      },
    };

    return selectDetails;
  }

  /**
   * Test does select element has optgroups
   * @returns {boolean} true if select element has optgroups
   */
  _hasOptGroups() {
    return this.element.querySelectorAll('optgroup').length > 0;
  }

  /**
   * Get all following siblings of each element up to but not including the element matched by the selector, DOM node, or jQuery object passed.
   *
   * VanillaJS alternative to jQuery function nextUntil()
   * https://api.jquery.com/nextuntil/
   *
   * @param {HTMLElement} elem - A DOM node indicating where to stop matching following sibling elements.
   * @param {String} selector - A string containing a selector expression to indicate where to stop matching following sibling elements.
   * @param {String} filter - A string containing a selector expression to match elements against.
   * @return {HTMLElement[]} sibling elements
   */
  _nextUntil(elem, selector, filter) {
    const siblings = [];
    elem = elem.nextElementSibling;

    while (elem) {
      if (elem.matches(selector)) {
        break;
      }

      if (filter && !elem.matches(filter)) {
        elem = elem.nextElementSibling;
        continue;
      }

      siblings.push(elem);
      elem = elem.nextElementSibling;
    }

    return siblings;
  }
}

function init() {
  const multiselectHtmlElements = document.getElementsByClassName('remultiselect');

  for (let i = 0; i < multiselectHtmlElements.length; i++) {
    let htmlElement = multiselectHtmlElements[i];
    if (!Object.prototype.hasOwnProperty.call(multiselects, htmlElement.id)) {
      multiselects[htmlElement.id] = new ReMultiselect(htmlElement);
    }
  }
}

function reinit(remultiselectElement) {
  if (typeof window.multiselects !== 'undefined' && window.multiselects[remultiselectElement.id]) {
    delete window.multiselects[remultiselectElement.id];
  }
  multiselects[remultiselectElement.id] = new ReMultiselect(remultiselectElement);
}

Object.assign(ReMultiselect.prototype, keyboardNavigable, nonRollingControlMode);

export default {
  init,
  reinit,
};
